import { Activity, useMemo, useState } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';

import { DEFAULT_PAGE_INDEX } from '@/app/constants/pagination';
import { podcastDetailsQueryOptions } from '@/app/queries/projects/podcastDetails';
import { Button } from '@/common/components/elements/button';
import { Title } from '@/common/components/elements/title';
import { EmptyState } from '@/common/components/features/empty-state';
import { Can } from '@/common/components/features/role-management';
import { Thumbnail } from '@/common/components/modules/thumbnail';
import { dateUtils } from '@/infra/protocols/date';
import { CreateEpisodeManager } from '@/modules/episodes/components/create-episode-manager';
import { EpisodeList } from '@/modules/episodes/components/episode-list';
import { EpisodeMobileList } from '@/modules/episodes/components/episode-list/episode-mobile-list';
import { useGetPaginatedEpisodesQuery } from '@/modules/episodes/queries/use-get-paginated-episodes-query';
import { ConfigurePodcastModal } from '@/modules/podcasts/components/configure-podcast-modal';
import { useGetPodcastConfigurationQuery } from '@/modules/podcasts/queries/use-get-podcast-configuration-query';

const PAGE_SIZE = import.meta.env.MODE === 'test' ? 3 : 10;

const Podcast = () => {
  const { projeto: project, podcast: podcastId } = Route.useParams();

  const { error: errorPodcastConfiguration } = useGetPodcastConfigurationQuery({
    podcastId,
    projectId: project,
  });

  const {
    data: { description, name: podcastName, thumbnail: podcastThumbnail },
  } = useSuspenseQuery(podcastDetailsQueryOptions(project, podcastId));

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: PAGE_SIZE,
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const {
    data: episodes,
    error: errorEpisodes,
    refetch: refetchEpisodes,
    isLoading: isLoadingEpisodes,
  } = useGetPaginatedEpisodesQuery({
    projectId: project,
    podcastId: podcastId,
    page: pagination.pageIndex,
  });

  const suggestedEpisodeName = useMemo(() => {
    const total = episodes?.totalItems ?? 0;
    const nextNumber = total + 1;
    const today = dateUtils.format(new Date(), 'dd/MM/yyyy');
    return `${podcastName} | ${today} | #${nextNumber}`;
  }, [episodes?.totalItems, podcastName]);

  const isEmptyEpisodeList = useMemo(
    () => !episodes?.data || episodes?.data?.length === 0,
    [episodes?.data],
  );

  const hasEpisodeError = useMemo(
    () => !!errorEpisodes && !isLoadingEpisodes,
    [errorEpisodes, isLoadingEpisodes],
  );

  const isEmptyList = useMemo(
    () => isEmptyEpisodeList && !errorEpisodes && !isLoadingEpisodes,
    [isEmptyEpisodeList, errorEpisodes, isLoadingEpisodes],
  );

  const shouldShowTable = useMemo(
    () => isLoadingEpisodes || (!errorEpisodes && !isEmptyEpisodeList),
    [isLoadingEpisodes, errorEpisodes, isEmptyEpisodeList],
  );

  const hasOnboardingConfigured = useMemo(
    () => !errorPodcastConfiguration && !isLoadingEpisodes,
    [errorPodcastConfiguration, isLoadingEpisodes],
  );

  const openModal = () => setIsCreateModalOpen(true);

  const closeModal = () => setIsCreateModalOpen(false);

  return (
    <>
      <div className='flex flex-col w-full h-full gap-6'>
        <div className='flex flex-col gap-3'>
          <div className='flex w-full py-5 flex-col mb:flex-row'>
            <div className='flex items-center gap-6'>
              <Thumbnail src={podcastThumbnail} alt={podcastName} />

              <div className='flex flex-col gap-1'>
                <Title className='capitalize'>{podcastName}</Title>

                <p className='text-sm text-content-neutral-primary whitespace-break-spaces'>
                  {description}
                </p>
              </div>
            </div>
            <div className='ml-auto flex items-center'>
              <Can I='create' a='Podcast'>
                {hasOnboardingConfigured && (
                  <Button size='small' variant='secondary' onClick={openModal}>
                    Editar Onboarding
                  </Button>
                )}
              </Can>
            </div>
          </div>

          <span className='flex items-center'>
            <span className='h-px flex-1 bg-border-neutral'></span>
          </span>

          <div className='flex flex-col gap-6'>
            <div className='flex items-center justify-between'>
              <h3 className='text-2xl text-content-neutral-primary font-medium'>Episódios</h3>

              <Can I='create' a='Episode'>
                <CreateEpisodeManager
                  projectId={project}
                  podcastId={podcastId}
                  suggestedName={suggestedEpisodeName}
                >
                  {({ open }) => (
                    <Button
                      animated
                      size='small'
                      variant='primary'
                      onClick={open}
                      disabled={isLoadingEpisodes || !!errorPodcastConfiguration}
                    >
                      Criar novo
                    </Button>
                  )}
                </CreateEpisodeManager>
              </Can>
            </div>

            {shouldShowTable && (
              <div>
                <div className='hidden sm:flex'>
                  <EpisodeList
                    data={episodes?.data}
                    pagination={pagination}
                    loading={isLoadingEpisodes}
                    setPagination={setPagination}
                    totalItems={episodes?.totalItems}
                  />
                </div>
                <div className='sm:hidden'>
                  <EpisodeMobileList
                    data={episodes?.data}
                    pagination={pagination}
                    loading={isLoadingEpisodes}
                    setPagination={setPagination}
                    totalItems={episodes?.totalItems}
                  />
                </div>
              </div>
            )}

            {hasEpisodeError && (
              <EmptyState
                image={{
                  src: '/images/empty-image.webp',
                  alt: 'Ilustração representando erro ao carregar episódios',
                }}
                title='Erro ao carregar episódios'
                description='Ocorreu um problema ao buscar os episódios do podcast. Verifique sua conexão com a internet e tente novamente.'
                actions={
                  <Button size='large' variant='primary' onClick={() => refetchEpisodes()}>
                    Tentar novamente
                  </Button>
                }
              />
            )}

            <Activity
              mode={isEmptyList && hasOnboardingConfigured ? 'visible' : 'hidden'}
              key='activity-empty'
            >
              <EmptyState
                image={{
                  src: '/images/empty-image.webp',
                  alt: 'Ilustração representando ausência de episódios',
                }}
                title='Nenhum episódio encontrado'
                description='Este podcast ainda não possui episódios cadastrados.'
              />
            </Activity>

            <Activity
              mode={isEmptyList && !hasOnboardingConfigured ? 'visible' : 'hidden'}
              key='activity-onboarding'
            >
              <EmptyState
                image={{
                  src: '/images/error-image.webp',
                  alt: 'Ilustração representando a necessidade de realizar o onboarding do podcast',
                }}
                title='Onboarding pendente'
                description='Para começar, conclua o onboarding do podcast. Depois, você poderá criar episódios.'
                actions={
                  <Can I='create' a='Podcast'>
                    <Button animated size='medium' variant='primary' onClick={openModal}>
                      Iniciar Onboarding
                    </Button>
                  </Can>
                }
              />
            </Activity>
          </div>
        </div>
      </div>

      <ConfigurePodcastModal
        onClose={closeModal}
        projectId={project}
        podcastId={podcastId}
        isOpen={isCreateModalOpen}
      />
    </>
  );
};

export const Route = createFileRoute('/_app/_dashboard/projetos/$projeto/podcast/$podcast/')({
  component: Podcast,
});
